<template>
    <v-container>
        <v-row>
            <v-col cols="12">
                <ValidatableTextField v-model:form="form" label="Name" :field="'name'" />
            </v-col>
            <v-col cols="12">
                <Autocomplete v-model:selected-items="form.steward_id" v-model:form="form" :field-name="'steward_id'" :items="stewards" label="Steward" :loading="fetchingStewards" />
            </v-col>
            <v-col cols="12">
                <Autocomplete v-model:selected-items="form.approval_authority_id" v-model:form="form" :field-name="'approval_authority_id'" :items="approvalAuthorities" label="Approval Authority" :loading="fetchingApprovalAuthorities" />
            </v-col>
            <v-col cols="6">
                <DatePicker v-model:field="form.start_date" v-model:form="form" label="Start Date" :field-name="'start_date'" />
            </v-col>
            <v-col cols="6">
                <DatePicker v-model:field="form.end_date" v-model:form="form" label="End Date" :field-name="'end_date'" />
            </v-col>
            <v-col cols="12">
                <Autocomplete v-model:selected-items="form.property_ids" :disabled="!form.steward_id" field-name="property_ids" item-title="property_code" item-value="id" :items="properties" label="Properties" multiple :loading="fetchingProperties" />
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
import { useInjectSubmitForm } from '@shared/composables/action-dialog/useInjectSubmitForm';
import { useProjectRepository } from '@app/land-hold/project/repositories/useProjectRepository';
import type { IProject } from '@app/land-hold/project/models/Project';
import { usePropertyRepository } from '@app/land-hold/property/repositories/usePropertyRepository.ts';
import type { IProperty } from '@app/land-hold/property/models/Property.ts';
import { useStewardRepository } from '@app/land-hold/steward/repositories/useStewardRepository.ts';
import { useApprovalAuthorityRepository } from '@app/land-hold/approval-authority/repositories/useApprovalAuthorityRepository.ts';

const props = defineProps({
    item: {
        type: Object as PropType<IProject>,
        required: true,
    },
});

const store = useProjectRepository<IProject & { property_ids: string[] }>().command.store({
    ...props.item,
    property_ids: [],
});
const form = store.form;
useInjectSubmitForm(store.execute);

const properties = ref<IProperty[]>([]);
const fetchingProperties = ref(false);
let stopDataWatch: (() => void) | null = null;
let stopProcessingWatch: (() => void) | null = null;

watch(
    () => form.steward_id,
    (stewardId) => {
        stopDataWatch?.();
        stopProcessingWatch?.();

        if (stewardId) {
            const { data, processing } = usePropertyRepository().query.listAllowedFor(stewardId);
            stopDataWatch = watch(
                data,
                (v) => {
                    properties.value = v;
                },
                { immediate: true },
            );
            stopProcessingWatch = watch(
                processing,
                (v) => {
                    fetchingProperties.value = v;
                },
                { immediate: true },
            );
        } else {
            properties.value = [];
            fetchingProperties.value = false;
        }
        form.property_ids = [];
    },
);

onUnmounted(() => {
    stopDataWatch?.();
    stopProcessingWatch?.();
});

const { stewards, fetchingStewards } = useStewardRepository().query.list();
const { approvalAuthorities, fetchingApprovalAuthorities } = useApprovalAuthorityRepository().query.list();
</script>
